import React, {
    TextareaHTMLAttributes,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react'

type AutoResizeTextareaProps = Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    'rows'
> & {
    minRows?: number
    maxRows?: number
}

const AutoResizeTextarea = React.forwardRef<
    HTMLTextAreaElement,
    AutoResizeTextareaProps
>(function AutoResizeTextarea(
    {
        minRows = 1,
        maxRows = 10,
        className,
        style,
        onChange,
        value,
        ...textareaProps
    },
    forwardedRef,
) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null)

    const setRef = useCallback(
        (node: HTMLTextAreaElement | null) => {
            innerRef.current = node
            if (typeof forwardedRef === 'function') {
                forwardedRef(node)
            } else if (forwardedRef) {
                forwardedRef.current = node
            }
        },
        [forwardedRef],
    )

    const resize = useCallback(() => {
        const el = innerRef.current
        if (!el) return

        el.style.height = 'auto'

        const computed = window.getComputedStyle(el)
        const lineHeight = parseFloat(computed.lineHeight || '0') || 16
        const paddingOffset =
            parseFloat(computed.paddingTop || '0') +
            parseFloat(computed.paddingBottom || '0')
        const borderOffset =
            parseFloat(computed.borderTopWidth || '0') +
            parseFloat(computed.borderBottomWidth || '0')

        const minHeight = minRows * lineHeight + paddingOffset + borderOffset
        const maxHeight = maxRows * lineHeight + paddingOffset + borderOffset

        const nextHeight = Math.min(
            Math.max(el.scrollHeight, minHeight),
            maxHeight,
        )

        el.style.height = `${nextHeight}px`
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
    }, [maxRows, minRows])

    useLayoutEffect(() => {
        resize()
    }, [resize])

    useEffect(() => {
        resize()
    }, [value, resize])

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            onChange?.(event)
            requestAnimationFrame(resize)
        },
        [onChange, resize],
    )

    return (
        <textarea
            {...textareaProps}
            ref={setRef}
            value={value}
            onChange={handleChange}
            className={className}
            rows={minRows}
            style={{ ...style, resize: 'none' }}
        />
    )
})

export default AutoResizeTextarea
